import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, User, ArrowRight, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { blogPosts } from '../utils/mockData';

import usePageSeo from '../hooks/usePageSeo';
const Blog = () => {
  usePageSeo({
    title: 'Blog | UAE Audit, Tax & Advisory Insights',
    description: 'Latest insights on UAE Corporate Tax, VAT, audit standards and business advisory — by White Paper Accounts Auditing experts.',
    path: '/blog'
  });

  const categories = ['All', ...new Set(blogPosts.map((p) => p.category))];
  const [selectedCategory, setSelectedCategory] = React.useState('All');

  const filteredPosts = selectedCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const featuredPost = blogPosts[0];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-teal-50 via-white to-gray-50">
        <div className="container mx-auto text-center">
          <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Insights & Resources</span>
          <h1 className="text-5xl font-bold text-gray-900 mt-4 mb-6">
            Latest News & Expert Insights
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Stay updated with the latest developments in UAE audit, tax, and advisory services. Expert guidance from the White Paper team.
          </p>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-12 px-4 bg-white">
        <div className="container mx-auto">
          <Link to={`/blog/${featuredPost.slug || featuredPost.id}`}>
            <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl overflow-hidden shadow-2xl cursor-pointer hover:shadow-3xl transition-all">
              <div className="grid lg:grid-cols-2">
                <div className="relative h-96 lg:h-auto bg-gray-50">
                  <img
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    loading="eager"
                    fetchPriority="high"
                    className="w-full h-full object-contain"
                  />
                  <div className="absolute top-6 left-6">
                    <Badge className="bg-yellow-400 text-gray-900 hover:bg-yellow-500">Featured</Badge>
                  </div>
                </div>
                <div className="p-12 text-white flex flex-col justify-center">
                  <Badge className="bg-white/20 text-white w-fit mb-4">{featuredPost.category}</Badge>
                  <h2 className="text-3xl font-bold mb-4 leading-tight">{featuredPost.title}</h2>
                  <p className="text-teal-100 text-lg mb-6 leading-relaxed">{featuredPost.excerpt}</p>
                  <div className="flex items-center space-x-6 mb-6 text-sm text-teal-100">
                    <div className="flex items-center space-x-2">
                      <User size={16} />
                      <span>{featuredPost.author}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar size={16} />
                      <span>{featuredPost.date}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock size={16} />
                      <span>{featuredPost.readTime}</span>
                    </div>
                  </div>
                  <Button className="bg-white text-teal-600 hover:bg-gray-100 w-fit group">
                    Read Full Article
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                  </Button>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-4 bg-gray-50 border-y">
        <div className="container mx-auto">
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Tag className="text-gray-600" size={20} />
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-teal-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border-2'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <Link key={post.id} to={`/blog/${post.slug || post.id}`}>
                <Card className="border-2 hover:border-teal-500 transition-all hover:shadow-lg group overflow-hidden cursor-pointer">
                  <div className="relative h-56 overflow-hidden bg-gray-50">
                    <img
                      src={post.image}
                      alt={post.title}
                      loading="lazy"
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-white text-gray-900 hover:bg-gray-100">{post.category}</Badge>
                    </div>
                  </div>
                  
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight group-hover:text-teal-600 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">{post.excerpt}</p>
                    
                    <div className="flex items-center space-x-4 text-xs text-gray-500 mb-4">
                      <div className="flex items-center space-x-1">
                        <User size={14} />
                        <span>{post.author}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar size={14} />
                        <span>{post.date}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock size={14} />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                    
                    <Button variant="ghost" className="text-teal-600 hover:text-teal-700 hover:bg-teal-50 w-full group/btn">
                      Read More
                      <ArrowRight className="ml-2 group-hover/btn:translate-x-1 transition-transform" size={16} />
                    </Button>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Stay Informed</h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Subscribe to our newsletter for weekly insights on UAE tax, audit, and advisory updates delivered to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-6 py-4 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
            />
            <Button size="lg" className="bg-white text-teal-600 hover:bg-gray-100 px-8 whitespace-nowrap">
              Subscribe Now
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
